import { Link } from "react-router-dom";
import { IconTrash, IconChevronRight } from "./Icons";
import { CaseStatusBadge } from "./CaseStatusBadge";
import { CaseRowShieldToggle } from "./CaseRowShieldToggle";
import { Case } from "../../types/home";
import {
  extractPrimaryRequest,
  formatDate,
  getCaseStatus,
} from "../../utils/homeCaseGrouping";
import { normalizeInsurer } from "../../utils/submissionValidation";

// ── Star icon ───────────────────────────────────────────────

function IconStar({
  className = "w-4 h-4",
  filled = false,
}: {
  className?: string;
  filled?: boolean;
}) {
  return filled ? (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"
        clipRule="evenodd"
      />
    </svg>
  ) : (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
      />
    </svg>
  );
}

// ── Status-aware action for the right side ──────────────────

function CaseRowAction({ caseData }: { caseData: Case }) {
  const status = caseData.case_status || "draft";

  switch (status) {
    case "draft":
      return (
        <Link
          to={`/new?mode=prior-auth&edit=${caseData.id}`}
          onClick={(e) => e.stopPropagation()}
          className="text-xs font-semibold text-slate-400 hover:text-teal-600 transition-colors whitespace-nowrap"
        >
          Generate <span className="font-normal">→</span>
        </Link>
      );

    case "letter_ready":
      return null;

    case "denied":
    case "appealed":
      return <CaseRowShieldToggle caseId={caseData.id} />;

    case "submitted":
    case "pending":
    case "approved":
    default:
      return null;
  }
}

// ============================================
// CASE ROW (Compact for panels)
// ============================================

export interface CaseRowProps {
  caseData: Case;
  onDelete: (e: React.MouseEvent) => void;
  onStar?: (caseId: string, starred: boolean) => void;
}

export function CaseRow({ caseData, onDelete, onStar }: CaseRowProps) {
  const primaryRequest = extractPrimaryRequest(
    caseData.request_type,
    caseData.request_details
  );
  const status = getCaseStatus(caseData);
  const isStarred = caseData.is_starred ?? false;

  return (
    <div className="flex items-center border-b border-slate-50 last:border-b-0 group">
      {/* Star toggle — always visible, left of content */}
      {onStar && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onStar(caseData.id, !isStarred);
          }}
          className={`
            flex-shrink-0 p-2.5 pl-3
            transition-colors duration-150
            focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:rounded-lg
            ${
              isStarred
                ? "text-amber-400 hover:text-amber-500"
                : "text-slate-300 hover:text-amber-400"
            }
          `}
          aria-label={isStarred ? "Remove star" : "Star this case"}
          aria-pressed={isStarred}
        >
          <IconStar className="w-4 h-4" filled={isStarred} />
        </button>
      )}

      {/* Clickable row — navigates to case */}
      <Link
        to={`/case/${caseData.id}`}
        className={`
          flex-1 flex items-center gap-3 pr-4 py-3 min-w-0
          hover:bg-slate-50 transition-colors
          ${onStar ? "pl-1" : "pl-4"}
        `}
      >
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-[15px] text-slate-900 group-hover:text-teal-600 transition-colors truncate leading-snug">
              {primaryRequest || caseData.request_type || "New Case"}
            </h4>
            <CaseStatusBadge
              status={status}
              caseStatus={caseData.case_status}
            />
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
            {caseData.diagnoses && (
              <span className="truncate text-slate-500">
                {caseData.diagnoses}
              </span>
            )}
            {caseData.diagnoses && caseData.insurance_company && (
              <span className="text-slate-300">·</span>
            )}
            {caseData.insurance_company && (
              <span className="truncate">
                {normalizeInsurer(caseData.insurance_company) ||
                  caseData.insurance_company}
              </span>
            )}
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-slate-400">
            {formatDate(caseData.created_at)}
          </span>

          {/* Status-aware action */}
          <CaseRowAction caseData={caseData} />

          <button
            onClick={onDelete}
            className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
            title="Delete case"
          >
            <IconTrash className="w-4 h-4" />
          </button>
          <IconChevronRight className="w-4 h-4 text-slate-300 group-hover:text-teal-500 transition-colors" />
        </div>
      </Link>
    </div>
  );
}
